import { useMutation, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import clsx from 'clsx';
import {
  ArrowRight,
  Check,
  CircleCheckBig,
  Download,
  FileUp,
  Link as LinkIcon,
  Mail,
  Pencil,
  QrCode,
  Users,
  X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileUploader } from 'react-drag-drop-files';

import { GetPageHomeDocument, Language, ShareFeature, UpdatePagePreferencesDocument } from '@/__generated__/graphql.js';
import useClickOutside from '@/hooks/useClickOutside.js';
import { useAssetOnboarding } from '@/onboarding_tour/TourProvider.js';
import { useCurrentUser } from '@/providers/CurrentUserProvider.js';
import { SEND_SHARE_WITH_TENANTS } from '@/services/page.service.js';
import { GET_MYHIVE_ONBOARDING } from '@/services/user.service.js';
import { extractSlug, isValidSlug } from '@/utils/helpers.js';

import { myhiveLocalization, MyHiveLocalizationKey } from '../../../localization/my-hive.js';
import { LanguageContext } from '../../../providers/LanguageProvider.js';
import MiniSpinner from './mini-spinner.js';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MAX_EMAILS = 1000;
const MAX_MESSAGE_CHARS = 500;
const MYHIVE_HOST = 'myhive.alveole.buzz';

const extractValidEmails = (text: string): string[] => {
  const matches = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) ?? [];
  const uniqueEmails = Array.from(new Set(matches));
  return uniqueEmails.filter((email) => emailRegex.test(email));
};

type ShareTab = 'email' | 'link' | 'qr';

interface ShareTenantsModalProps {
  open: boolean;
  onClose: () => void;
  pageId: string;
  pageSlug: string;
  pageTitle: string;
}

const ShareTenantsModal: React.FC<ShareTenantsModalProps> = ({ open, onClose, pageId, pageSlug, pageTitle }) => {
  const { isLoading: isLoadingAuth } = useAuth0();
  const { user } = useCurrentUser();
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const { paused, setPaused } = useAssetOnboarding();

  const [sendShareWithTenants, { loading: sendingShare, error: sendError, reset: resetShare }] =
    useMutation(SEND_SHARE_WITH_TENANTS);
  const [updatePage, { loading: saving }] = useMutation(UpdatePagePreferencesDocument);

  // The full preference set is needed to save name/slug without clobbering other settings.
  // Cache hit on the asset page (asset-home already runs this query); a background fetch elsewhere.
  const { data: pageData } = useQuery(GetPageHomeDocument, {
    variables: { slug: pageSlug },
    skip: !open || !pageSlug,
  });
  const page = pageData?.page;

  const [tab, setTab] = useState<ShareTab>('email');
  const [emails, setEmails] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [step, setStep] = useState<'compose' | 'preview' | 'sent' | null>('compose');
  const [copied, setCopied] = useState(false);

  // "Make it yours" — `currentSlug` drives the shareable link/QR (saved value); `form*` are the edits.
  const [currentSlug, setCurrentSlug] = useState(pageSlug);
  const [formTitle, setFormTitle] = useState(pageTitle);
  const [formSlug, setFormSlug] = useState(pageSlug);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  const modalRef = React.useRef<HTMLDivElement>(null);
  const qrCodeRef = React.useRef<SVGSVGElement>(null);

  const senderName = user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : (user?.email ?? '');
  // Counted with the same extractor handleSend uses, so the cap gate matches what actually gets
  // sent; splitting on separators alone undercounts wrapped forms like "Name <a@b.com>".
  const emailsCount = extractValidEmails(emails).length;
  const loading = isLoadingAuth || sendingShare || saving;

  const defaultMessage = t[MyHiveLocalizationKey.CustomMessageText];
  const previewSubject = `${senderName} ${t[MyHiveLocalizationKey.PreviewSubjectText]}`;
  const myhiveUrl = `https://${MYHIVE_HOST}/${currentSlug}`;
  // Drop the trailing colon so the heading reads cleanly next to the right-aligned char counter.
  const messageHeading = t[MyHiveLocalizationKey.CustomMessage].replace(/\s*:\s*$/, '');

  const copyToClipboard = (): void => {
    navigator.clipboard
      .writeText(myhiveUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* no-op */
      });
  };

  // Re-pre-fill the textarea when the active default message changes (e.g. language switch).
  useEffect(() => {
    setCustomMessage(defaultMessage);
  }, [defaultMessage]);

  // Seed the link/QR + edit form from the latest props whenever they change or the modal reopens.
  useEffect(() => {
    setCurrentSlug(pageSlug);
    setFormSlug(pageSlug);
    setFormTitle(pageTitle);
  }, [pageSlug, pageTitle, open]);

  const closeModal = (e?: React.MouseEvent): void => {
    e?.stopPropagation();
    onClose();
    setStep('compose');
    setTab('email');
    setSaveError('');
    setSaved(false);
    resetShare();
  };

  useClickOutside(modalRef, () => {
    if (open) closeModal();
  });

  const handleComplete = (): void => {
    setEmails('');
    setCustomMessage(defaultMessage);
    setStep('sent');
    if (paused) {
      setTimeout(() => setPaused(false), 500);
      closeModal();
    }
  };

  const handleSend = async (): Promise<void> => {
    if (!user?.id) return;
    const tenantEmails = extractValidEmails(emails);
    const lang = language === 'fr' ? Language.Fr : Language.En;

    setPaused(true);
    await sendShareWithTenants({
      variables: {
        input: { pageId, tenantEmails, language: lang, message: customMessage, feature: ShareFeature.Feed },
      },
      onCompleted: handleComplete,
      refetchQueries: [GET_MYHIVE_ONBOARDING],
    });
  };

  const handleCsvUpload = async (file: File | File[]): Promise<void> => {
    if (file instanceof Array) return;
    const text = await file.text();
    setEmails((prev) => {
      const allEmails = Array.from(new Set([...extractValidEmails(prev), ...extractValidEmails(text)]));
      return allEmails.join(', ');
    });
  };

  const handleSave = async (): Promise<void> => {
    setSaveError('');
    setSaved(false);
    const cleanedSlug = extractSlug(formSlug);
    if (!formTitle.trim()) {
      setSaveError(t[MyHiveLocalizationKey.ThisFieldIsRequired]);
      return;
    }
    if (!cleanedSlug || !isValidSlug(cleanedSlug)) {
      setSaveError(t[MyHiveLocalizationKey.PreferencesHivePermalinkFormatErrorMessage]);
      return;
    }
    if (!page) return;

    const slugChanged = cleanedSlug !== currentSlug;
    try {
      await updatePage({
        refetchQueries: [GetPageHomeDocument],
        variables: {
          pageId,
          title: formTitle,
          description: page.description,
          language: page.language,
          searchVisibility: page.searchVisibility,
          slug: cleanedSlug,
          eventInterestEnabled: page.eventInterestEnabled,
          honeyInterestEnabled: page.honeyInterestEnabled,
          eventRegistrationEnabled: page.eventRegistrationEnabled,
          intercomChatBotEnabled: page.intercomChatBotEnabled,
          showUpcomingEvents: page.showUpcomingEvents,
          showUpcomingVisits: page.showUpcomingVisits,
          queenFeatureEnabled: page.queenFeatureEnabled,
          queenCommunityVotingEnabled: page.queenCommunityVotingEnabled,
        },
      });

      setCurrentSlug(cleanedSlug);
      setFormSlug(cleanedSlug);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);

      // The asset route is keyed off the slug, so a slug change makes the current URL stale —
      // reload at the new URL (mirrors AssetPreferences). In contexts not keyed by slug (e.g. the
      // portfolio dashboard) the pathname won't contain it, so we just update in place.
      if (slugChanged && window.location.pathname.includes(`/${currentSlug}`)) {
        const newPath = window.location.pathname.replace(`/${currentSlug}`, `/${cleanedSlug}`);
        window.location.assign(newPath + window.location.search);
      }
    } catch {
      setSaveError(
        cleanedSlug !== currentSlug
          ? `${t[MyHiveLocalizationKey.ApiError]} ${t[MyHiveLocalizationKey.SlugUnavailable]}`
          : t[MyHiveLocalizationKey.ApiError],
      );
    }
  };

  const handleDownloadQRCode = (): void => {
    const svg = qrCodeRef.current;
    if (!svg) return;
    try {
      const canvas = document.createElement('canvas');
      const svgRect = svg.getBoundingClientRect();
      canvas.width = svgRect.width;
      canvas.height = svgRect.height;
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = 'RGBA(255, 255, 255, 0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `qr-code-${currentSlug}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      img.src = URL.createObjectURL(svgBlob);
    } catch (err) {
      console.error('Error in QR code download process:', err);
    }
  };

  if (!open) return null;

  const tabs: Array<{ key: ShareTab; label: string; icon: typeof Mail }> = [
    { key: 'email', label: t[MyHiveLocalizationKey.InviteByEmailTab], icon: Mail },
    { key: 'link', label: t[MyHiveLocalizationKey.ShareALinkTab], icon: LinkIcon },
    { key: 'qr', label: t[MyHiveLocalizationKey.QRCodeTab], icon: QrCode },
  ];

  return createPortal(
    <div
      id="share-modal-compose-click-away"
      className="cursor-default fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-scroll bg-black/50"
    >
      <div
        ref={modalRef}
        className="flex h-dvh max-h-dvh w-screen max-w-none flex-col overflow-hidden rounded-none bg-white shadow-xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-full sm:max-w-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dark header */}
        <div className="relative bg-brand-teal-dark-500 px-6 pb-0 pt-6 text-white">
          <div className="mb-4">
            <button
              id="share-modal-close-button"
              onClick={closeModal}
              aria-label={t[MyHiveLocalizationKey.Close]}
              className="absolute right-5 top-5 cursor-pointer text-white/70 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="flex items-center gap-2 text-xl sm:text-2xl">
              <Users className="h-6 w-6 text-brand-yellow" />
              {t[MyHiveLocalizationKey.ShareWithTenantsTitle]}
            </h2>
            <p className="mt-2 max-w-md text-sm text-white/80">{t[MyHiveLocalizationKey.ShareWithTenantsSubheader]}</p>
          </div>

          {step === 'compose' && (
            <div className="-mb-px mt-4 flex sm:gap-10 gap-6">
              {tabs.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  id={`share-modal-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={clsx(
                    'flex cursor-pointer items-center gap-2 border-b-2 pb-3 text-base transition-colors',
                    tab === key
                      ? 'border-brand-yellow font-medium text-white'
                      : 'border-transparent text-white/70 hover:text-white',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="relative flex flex-1 flex-col gap-2 overflow-y-auto p-6">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/25">
              <MiniSpinner />
            </div>
          )}

          {step === 'compose' && tab === 'email' && (
            <>
              <div className="flex flex-col gap-5 max-sm:min-h-0 max-sm:flex-1">
                {/* Email addresses */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-md font-medium text-black mb-2">{t[MyHiveLocalizationKey.EmailAddresses]}</p>
                    <span
                      className={clsx('text-sm tabular-nums text-gray-500', {
                        'text-red-500': emailsCount > MAX_EMAILS,
                      })}
                    >
                      {emailsCount} / {MAX_EMAILS}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{t[MyHiveLocalizationKey.EmailAddressesHint]}</p>
                  <textarea
                    id="share-modal-compose-email-input"
                    className="min-h-28 max-h-40 w-full resize-none overflow-auto whitespace-pre-wrap rounded-lg border border-gray-300 p-3 focus:border-alveole-gold focus:outline-none"
                    placeholder={t[MyHiveLocalizationKey.EmailAddressesPlaceholder]}
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    onBlur={() => setEmails(extractValidEmails(emails).join(', '))}
                  />
                  <div className="mt-2">
                    <FileUploader
                      classes="!outline-hidden"
                      handleChange={(file) => {
                        void handleCsvUpload(file);
                      }}
                      multiple={false}
                      types={['csv']}
                    >
                      <button
                        id="share-modal-compose-add-csv-button"
                        className="inline-flex cursor-pointer items-center gap-2 border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        <FileUp className="h-4 w-4" />
                        {t[MyHiveLocalizationKey.AddFromCsv]}
                      </button>
                    </FileUploader>
                  </div>
                </div>

                {/* Custom message */}
                <div className="flex flex-col gap-1 max-sm:min-h-0 max-sm:flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-md mb-2 font-medium text-black">{messageHeading}</p>
                    <span
                      className={clsx('text-sm tabular-nums text-gray-500', {
                        'text-red-500': customMessage.length >= MAX_MESSAGE_CHARS,
                      })}
                    >
                      {customMessage.length} / {MAX_MESSAGE_CHARS}
                    </span>
                  </div>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="min-h-42 w-full resize-none overflow-auto rounded-lg border border-gray-300 p-3 focus:border-alveole-gold focus:outline-none max-sm:min-h-0 max-sm:flex-1"
                    maxLength={MAX_MESSAGE_CHARS}
                  />
                </div>
              </div>

              <div className="-mx-6 mt-auto flex justify-end gap-2 border-t border-gray-100 px-6 pt-4">
                <button
                  id="share-modal-compose-cancel-button"
                  onClick={closeModal}
                  className="inline-flex cursor-pointer items-center gap-2 border border-alveole-800 px-5 py-2.5 text-sm font-medium text-alveole-800 transition-colors hover:bg-gray-50"
                >
                  {t[MyHiveLocalizationKey.Cancel]}
                </button>
                <button
                  id="share-modal-compose-preview-button"
                  disabled={emailsCount === 0 || emailsCount > MAX_EMAILS}
                  className="inline-flex cursor-pointer items-center gap-2 bg-brand-yellow px-5 py-2.5 text-sm font-medium text-black transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setStep('preview')}
                >
                  {t[MyHiveLocalizationKey.PreviewSend]}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          )}

          {step === 'compose' && tab === 'link' && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <p className="text-md font-medium text-black">{t[MyHiveLocalizationKey.ShareableLink]}</p>
                <p className="text-sm text-gray-500">{t[MyHiveLocalizationKey.ShareableLinkHint]}</p>
                <div className="mt-1 flex">
                  <input
                    id="share-modal-link-input"
                    readOnly
                    value={`${MYHIVE_HOST}/${currentSlug}`}
                    className="flex-1 overflow-x-auto border rounded-l-lg border-gray-300 px-3 py-2 text-gray-700 focus:outline-none"
                  />
                  <button
                    id="share-modal-link-copy-button"
                    onClick={copyToClipboard}
                    className="inline-flex cursor-pointer rounded-r-lg border-l-0 border border-gray-300 items-center gap-1 whitespace-nowrap bg-brand-yellow px-5 text-sm font-medium text-black transition-colors hover:brightness-95"
                  >
                    {t[MyHiveLocalizationKey.CopyLink]}
                    {copied && <Check className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-3 rounded-lg bg-alveole-100 p-4">
                <div className="flex flex-col gap-0.5">
                  <p className="text-md flex items-center gap-1.5 font-medium text-black">
                    <Pencil className="h-4 w-4 text-alveole-green" />
                    {t[MyHiveLocalizationKey.MakeItYours]}
                  </p>
                  <p className="text-sm text-gray-500">{t[MyHiveLocalizationKey.MakeItYoursHint]}</p>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="share-modal-page-name-input" className="text-sm font-medium text-gray-900">
                    {t[MyHiveLocalizationKey.PageNameLabel]}
                  </label>
                  <input
                    id="share-modal-page-name-input"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white p-2 focus:border-alveole-gold focus:outline-none"
                  />
                  <p className="text-xs text-gray-500">{t[MyHiveLocalizationKey.PageNameExampleHint]}</p>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="share-modal-page-link-input" className="text-sm font-medium text-gray-900">
                    {t[MyHiveLocalizationKey.PageLinkLabel]}
                  </label>
                  <div
                    className={clsx(
                      'flex items-center rounded-lg border bg-white px-3 focus-within:border-alveole-gold',
                      saveError ? 'border-red-500' : 'border-gray-300',
                    )}
                  >
                    <span className="whitespace-nowrap text-gray-500">{MYHIVE_HOST}/</span>
                    <input
                      id="share-modal-page-link-input"
                      value={formSlug}
                      onChange={(e) => setFormSlug(e.target.value)}
                      className="min-w-0 flex-1 py-2 font-medium text-brand-teal-500 focus:outline-none"
                    />
                  </div>
                  {saveError ? (
                    <p className="text-xs text-red-500">{saveError}</p>
                  ) : (
                    <p className="text-xs text-gray-500">{t[MyHiveLocalizationKey.PageLinkExampleHint]}</p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3">
                  {saved && (
                    <span className="flex items-center gap-1 text-sm text-brand-teal-500">
                      <Check className="h-4 w-4" />
                      {t[MyHiveLocalizationKey.SavedConfirmation]}
                    </span>
                  )}
                  <button
                    id="share-modal-save-button"
                    onClick={() => void handleSave()}
                    disabled={saving || !page}
                    className="inline-flex cursor-pointer items-center bg-brand-yellow px-5 py-2.5 text-sm font-medium text-black transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t[MyHiveLocalizationKey.Save]}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'compose' && tab === 'qr' && (
            <div className="flex flex-col items-center gap-4 py-2 sm:flex-row sm:items-start">
              <div className="shrink-0 rounded-lg border border-gray-200 p-3">
                <QRCodeSVG id="share-modal-qr-code" ref={qrCodeRef} value={myhiveUrl} size={150} fgColor="#000000" />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-md font-medium text-black">{t[MyHiveLocalizationKey.QRScanHeading]}</p>
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">
                    {MYHIVE_HOST}/{currentSlug}
                  </span>
                </p>
                <button
                  id="share-modal-qr-download-button"
                  onClick={handleDownloadQRCode}
                  className="inline-flex w-fit cursor-pointer items-center gap-2 bg-brand-yellow px-5 py-2.5 text-sm font-medium text-black transition-colors hover:brightness-95"
                >
                  {t[MyHiveLocalizationKey.DownloadQRCode]}
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <>
              <div className="flex-1">
                <div className="flex justify-between">
                  <h2 className="flex items-end text-lg font-semibold">
                    <Mail className="mb-0.5 mr-1 text-brand-teal-500" />
                    {t[MyHiveLocalizationKey.EmailPreview]}
                  </h2>
                </div>
              </div>
              <div className="flex-1 overflow-auto rounded-lg border border-gray-200 p-2">
                <p className="text-md text-gray-500">
                  <span className="text-md font-bold text-black">From: </span>product@alveole.buzz
                </p>
                <p className="text-md text-gray-500">
                  <span className="text-md font-bold text-black">{t[MyHiveLocalizationKey.PreviewTo]} </span>
                  {emails.split(',').slice(0, 2).join(', ')}
                  {emails.split(',').length > 2 &&
                    `, + ${emails.split(',').length - 2} ${t[MyHiveLocalizationKey.More]}`}
                </p>
                <p className="text-md text-gray-500">
                  <span className="text-md font-bold text-black"> {t[MyHiveLocalizationKey.PreviewSubject]}</span>{' '}
                  {previewSubject}
                </p>

                <p className="text-md font-bold text-black">{t[MyHiveLocalizationKey.BodyEmail]} </p>
                <div className="mt-2 flex flex-col gap-1 rounded-lg bg-gray-100 p-2">
                  <span className="text-md whitespace-pre-wrap text-gray-500">{customMessage || defaultMessage}</span>
                  <div className="text-md text-gray-500">
                    <p>
                      👉 {t[MyHiveLocalizationKey.PreviewClickToExplore]} {myhiveUrl}
                    </p>
                  </div>
                </div>
              </div>
              {sendError && <p className="mb-2 text-red-500">Error: {sendError.message}? Please try again later.</p>}
              <div className="mt-2 flex justify-end gap-2">
                <button
                  id="share-modal-preview-back-button"
                  onClick={() => setStep('compose')}
                  className="inline-flex cursor-pointer items-center gap-2 border border-alveole-800 px-5 py-2.5 text-sm font-medium text-alveole-800 transition-colors hover:bg-gray-50"
                >
                  {t[MyHiveLocalizationKey.BackToEdit]}
                </button>
                <button
                  id="share-modal-preview-send-button"
                  className="inline-flex cursor-pointer items-center gap-2 bg-brand-yellow px-5 py-2.5 text-sm font-medium text-black transition-colors hover:brightness-95"
                  onClick={() => void handleSend()}
                >
                  {t[MyHiveLocalizationKey.Send]}
                </button>
              </div>
            </>
          )}

          {step === 'sent' && (
            <>
              <CircleCheckBig className="mx-auto my-4 h-16 w-16 text-brand-teal-500" />
              <p className="text-center text-lg font-medium">{t[MyHiveLocalizationKey.ShareSuccess1]}</p>
              <div className="flex justify-end">
                <button
                  id="share-modal-sent-close-button"
                  className="mt-4 inline-flex cursor-pointer items-center bg-brand-teal-dark-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-teal-dark-400"
                  onClick={closeModal}
                >
                  {t[MyHiveLocalizationKey.Close]}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ShareTenantsModal;
